<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account-group</v-icon>
        Member Details
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Top Navigation Bar -->
        <div class="d-flex justify-end mb-4">
          <v-btn 
            v-if="canEditMember"
            color="#002060"
            dark
            rounded
            size="large"
            @click="editMember"
            :disabled="loading"
            class="mr-2"
          >
            <v-icon left>mdi-pencil</v-icon>
            Edit Member
          </v-btn>
          
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="$router.back()"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>

        <v-alert
          v-if="error"
          type="error"
          class="mb-4"
          variant="tonal"
        >
          {{ error }}
        </v-alert>

        <v-skeleton-loader
          v-if="loading"
          type="article, article"
          class="mb-6"
        />

        <div v-if="!loading && !notFound">
          <!-- Member Header -->
          <div class="text-center mb-6">
            <v-avatar size="80" color="#002060" class="mb-3">
              <v-icon size="40" color="white">mdi-account-group</v-icon>
            </v-avatar>
            <h2 class="member-name mb-2">{{ displayName }}</h2>
            <p class="member-email mb-3">{{ member?.email || '—' }}</p>
            <v-chip :color="statusColor" text-color="white" size="large">
              {{ statusText }}
            </v-chip>
          </div>

          <v-row>
            <!-- Basic Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Basic Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="displayName"
                label="Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="member?.email || '—'"
                label="Email"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="member?.memberType || '—'"
                label="Member Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-multiple"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="member?.membershipType || '—'"
                label="Membership Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-star"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="member?.dateAdded || '—'"
                label="Date Added"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-calendar"
                readonly
              />
            </v-col>
          </v-row>

          <v-row v-if="fullMember">
            <!-- Profile Details -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Profile Details</h3>
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                :model-value="fullMember.address || '—'"
                label="Address"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-home"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="fullMember.employeeId || '—'"
                label="Employee ID"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-account-details"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="fullMember.bankName || '—'"
                label="Bank"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-bank"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="fullMember.bankAccountNumber || '—'"
                label="Bank Account"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-credit-card"
                readonly
              />
            </v-col>
          </v-row>
          
          <v-row v-if="!fullMember">
            <v-col cols="12">
              <v-alert type="info" variant="tonal">
                Full profile not available yet. This member may have been newly added.
              </v-alert>
            </v-col>
          </v-row>
        </div>
        
        <div v-if="!loading && notFound" class="text-center py-8">
          <v-icon size="64" color="#ccc">mdi-account-search</v-icon>
          <h3 class="mt-2" style="color:#666; font-weight:600;">Member not found</h3>
          <p class="text-medium-emphasis">The selected member could not be found. It may have been removed.</p>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import NotificationDialog from '../components/NotificationDialog.vue';

export default {
  name: 'MemberDetails',
  components: {
    NotificationDialog
  },
  props: {
    id: { type: String, required: true }
  },
  data() {
    return {
      member: null,
      fullMember: null,
      loading: true,
      error: null,
      notFound: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: ''
    };
  },
  computed: {
    displayName() {
      if (!this.member) return '—';
      if (this.member.memberType === 'Business') {
        return this.member.businessName || '—';
      }
      const first = this.member.firstName || '';
      const last = this.member.surname || '';
      return `${first} ${last}`.trim() || '—';
    },
    statusText() {
      return this.member?.status || '—';
    },
    statusColor() {
      const s = (this.member?.status || '').toLowerCase();
      if (s === 'newly added member') return 'info';
      if (s === 'active') return 'success';
      if (s === 'inactive') return 'error';
      return 'warning';
    },
    canEditMember() {
      // Only Admin and Executive can edit members
      const userRole = this.$store.getters.userRole;
      return ['Admin', 'Executive'].includes(userRole);
    }
  },
  async created() {
    try {
      if (!this.id) {
        this.error = 'No member ID provided';
        this.notFound = true;
        return;
      }

      console.log('Loading member with ID:', this.id);
      // Load from newMembers by route id (document id)
      const nmRef = doc(db, 'newMembers', this.id);
      const nmSnap = await getDoc(nmRef);
      if (nmSnap.exists()) {
        this.member = { id: nmSnap.id, ...nmSnap.data() };
        console.log('Member loaded:', this.member);
      } else {
        console.error('Member document not found:', this.id);
        this.notFound = true;
      }

      // If memberId is stored in newMembers, try to load full member profile
      const fullMemberId = this.member?.memberId;
      if (fullMemberId) {
        const mRef = doc(db, 'members', fullMemberId);
        const mSnap = await getDoc(mRef);
        if (mSnap.exists()) {
          this.fullMember = { id: mSnap.id, ...mSnap.data() };
          console.log('Full member profile loaded:', this.fullMember);
        }
      }
    } catch (e) {
      this.error = 'Failed to load member details';
      console.error(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    editMember() {
      // Navigate to edit page based on member type
      const memberType = this.member?.memberType || 'Individual';
      let routeName = 'AddUser';
      
      switch (memberType) {
        case 'Business':
          routeName = 'AddUser'; // Assuming AddUser handles both individual and business
          break;
        default:
          routeName = 'AddUser';
      }
      
      // Navigate to the add form with the member data for editing
      this.$router.push({ 
        name: routeName, 
        query: { 
          edit: 'true', 
          memberId: this.id 
        } 
      });
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.member-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #002060;
  margin: 0;
}

.member-email {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style>


